import { IWatrRequest } from '@nkr/models/index';
import { DevMockBatchType, DevMockKind, DevMockReceiptInput } from './constants';

export interface DevMockSnapshot {
    seed: number;
    fullName: string;
    firstName: string;
    lastName: string;
    alias: string;
    ssn: string;
    ssnLast4: string;
    employer: string;
    incomeIrsYear: number;
    incomeIrsYearGross: number;
    incomePayStubGross: number;
    incomePayStubStartDate: string;
    incomePayStubEndDate: string;
    receiptAmount: number;
    receiptDate: string;
    vendor: string;
    matched: boolean;
}

function digitsOnly(v: string): string {
    return String(v || '').replace(/\D/g, '');
}

function formatSsn(digits: string): string {
    if (digits.length < 9) {
        return digits;
    }
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
}

function moneyOr(v: any, fallback: number): number {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : fallback;
}

export function buildDevMockSnapshot(
    data: IWatrRequest,
    receipt?: DevMockReceiptInput
): DevMockSnapshot {
    const firstName = (data.firstName || 'Poe').trim() || 'Poe';
    const lastName = (data.lastName || 'Skywalker').trim() || 'Skywalker';
    const fullName = (data.name || `${firstName} ${lastName}`).trim();
    const ssnDigits = digitsOnly(data.ssnDecrypted || '');
    const ssn = ssnDigits.length >= 9 ? formatSsn(ssnDigits) : '100-00-4074';
    const year = data.incomeIrsYear && data.incomeIrsYear > 1900 ? data.incomeIrsYear : 2022;
    return {
        seed: Number(data.id) || 42,
        fullName,
        firstName,
        lastName,
        alias: (data.alias || 'SW-TEST-01').trim() || 'SW-TEST-01',
        ssn,
        ssnLast4: ssn.replace(/\D/g, '').slice(-4),
        employer: 'Rebel Alliance Payroll',
        incomeIrsYear: year,
        incomeIrsYearGross: moneyOr(data.incomeIrsYearGross, 52005.74),
        incomePayStubGross: moneyOr(data.incomePayStubGross, 2105.67),
        incomePayStubStartDate: data.incomePayStubStartDate || `${year + 1}-01-01`,
        incomePayStubEndDate: data.incomePayStubEndDate || `${year + 1}-01-14`,
        receiptAmount: moneyOr(receipt && receipt.amount, 125.81),
        receiptDate: (receipt && receipt.expenseDate) || '2023-07-16',
        vendor: (receipt && receipt.description) || 'Mos Eisley Inn',
        matched: true
    };
}

function shouldMismatch(batchType: DevMockBatchType, index: number, seed: number): boolean {
    if (batchType === 'clean') {
        return false;
    }
    if (batchType === 'mixed') {
        return ((seed + index * 17) % 4) === 0;
    }
    return ((seed + index * 11) % 3) !== 0;
}

export function applyBatchVariant(
    base: DevMockSnapshot,
    kind: DevMockKind,
    batchType: DevMockBatchType,
    index: number
): DevMockSnapshot {
    if (!shouldMismatch(batchType, index, base.seed)) {
        return { ...base, matched: true };
    }

    const next: DevMockSnapshot = { ...base, matched: false };
    const salt = Math.abs(base.seed * 31 + index * 97);

    if (kind === 'ssn' || kind === 'tax' || kind === 'paystub') {
        const wrongDigits = String(200000000 + (salt % 700000000)).padStart(9, '0');
        next.ssn = formatSsn(wrongDigits);
        next.ssnLast4 = wrongDigits.slice(-4);
    }
    if (kind === 'tax' || kind === 'paystub') {
        next.firstName = 'Jordan';
        next.lastName = 'Reyes';
        next.fullName = 'Jordan Reyes';
        next.employer = 'Outer Rim Staffing LLC';
    }
    if (kind === 'tax') {
        next.incomeIrsYear = base.incomeIrsYear > 2000 ? base.incomeIrsYear - 1 : 2021;
        next.incomeIrsYearGross = Math.round((base.incomeIrsYearGross * 0.82 + (salt % 900) / 100) * 100) / 100;
    }
    if (kind === 'paystub') {
        next.incomePayStubGross = Math.round((base.incomePayStubGross * 1.35 + (salt % 500) / 100) * 100) / 100;
        next.incomePayStubStartDate = `${base.incomeIrsYear}-06-01`;
        next.incomePayStubEndDate = `${base.incomeIrsYear}-06-14`;
    }
    if (kind === 'receipt') {
        next.receiptAmount = Math.round((base.receiptAmount * 1.55 + (salt % 300) / 100) * 100) / 100;
        next.vendor = 'Unlisted Merchant #' + String((salt % 90) + 10);
        next.fullName = 'Guest Traveler';
    }
    return next;
}

export function fileNameForMock(kind: DevMockKind, requestId: number, batchType: DevMockBatchType): string {
    return `MOCK-${batchType}-${kind}-${requestId}-${Date.now()}.jpg`;
}
